import { Icon, IconName, IconSize, IconStrokeWidth } from "@/shared/components";
import { cn } from "@/shared/utils";
import { Slot, Slottable } from "@radix-ui/react-slot";

import { useHideButton } from "@/shared/hooks";
import * as React from "react";
import { MouseEventHandler, useState } from "react";
import { buttonVariants, ButtonVariants } from "./button-variants";

type AsyncMouseEventHandler = (event: React.MouseEvent<Element, MouseEvent>) => Promise<void>;

type ButtonProps = Omit<React.ComponentProps<"button">, "onClick"> &
    ButtonVariants & {
        asChild?: boolean;
        ignoreHideButton?: boolean;
        icon?: IconName;
        iconSize?: IconSize;
        iconStrokeWidth?: IconStrokeWidth;
        onClick?: MouseEventHandler | AsyncMouseEventHandler;
    };

export function Button({
    className,
    variant,
    size,
    destructive,
    inverse,
    asChild = false,
    ignoreHideButton = false,
    iconOnly = false,
    icon,
    iconSize,
    iconStrokeWidth,
    children,
    onClick,
    ...props
}: Readonly<ButtonProps>) {
    const hidden = useHideButton();
    const [loading, setLoading] = useState<boolean>(false);

    if (hidden && !ignoreHideButton) return null;

    const hasIcon = !!icon;
    const Component = asChild ? Slot : "button";

    const safeOnClick = (event: React.MouseEvent) => {
        setLoading(true);
        Promise.resolve(onClick?.(event))
            .catch((...args) => {
                console.error(`Button.onClick handler returned a Promise that rejected with arguments: ${JSON.stringify(args)}`);
                console.debug(...args);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <Component
            data-slot="button"
            className={cn(
                "flex items-center gap-1",
                buttonVariants({
                    variant,
                    destructive,
                    inverse,
                    size,
                    hasIcon,
                    iconOnly,
                }),
                className
            )}
            type="button"
            {...props}
            disabled={loading || props.disabled}
            onClick={safeOnClick}
        >
            {hasIcon && <Icon name={icon} size={iconSize} strokeWidth={iconStrokeWidth} />}
            <Slottable>{children}</Slottable>
            {loading && <Icon name="loader" size={iconSize} className="ml-1 animate-spin" />}
        </Component>
    );
}
